import Nav from "@/components/Nav";
import PageHero from "@/components/PageHero";
import MarketingFooter from "@/components/MarketingFooter";
import { Link } from "react-router-dom";
import { Moon, Sparkles, ArrowRight } from "lucide-react";
import { usePageMeta } from "@/lib/pageMeta";
import { motion } from "framer-motion";

const GUNAS = [
  { name: "Varna", desc: "Spiritual & psychological alignment", max: 1 },
  { name: "Vashya", desc: "Mutual attraction & harmony", max: 2 },
  { name: "Tara", desc: "Destiny compatibility (birth star)", max: 3 },
  { name: "Yoni", desc: "Physical & temperamental compatibility", max: 4 },
  { name: "Graha Maitri", desc: "Mental compatibility (planetary friendship)", max: 5 },
  { name: "Gana", desc: "Nature & temperament alignment", max: 6 },
  { name: "Bhakoot", desc: "Emotional & family life compatibility", max: 7 },
  { name: "Nadi", desc: "Genetic & progeny compatibility", max: 8 },
];

const AI_DIMENSIONS = [
  "Personality & emotional intelligence",
  "Communication style",
  "Attachment orientation",
  "Family values & traditions",
  "Financial goals & lifestyle",
  "Long-term life priorities",
  "Conflict-resolution style",
  "Growth & readiness",
];

export default function Kundali() {
  usePageMeta(
    "Kundali × AI Fusion",
    "Where Ashtakoot meets algorithm. Vedic Guna Milan blended with explainable AI compatibility — the completeness the elders asked for."
  );
  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Nav variant="marketing" />
      <PageHero
        overline="Kundali × AI Fusion"
        sanskrit="अष्टकूट + बुद्धि"
        title={<>Where the stars meet <span className="italic text-amber-300">the science</span> of a good marriage.</>}
        subtitle="For 5,000 years, Ashtakoot Guna Milan has guided Indian families through this decision. Modern relationship psychology has 60 years of evidence. Anubandh.ai is the first platform to fuse both — into a single Marriage Intelligence Report."
      >
        <div className="flex flex-wrap gap-3">
          <Link to="/signup" data-testid="kundali-signup-cta" className="btn-pill bg-amber-500 text-[#12103A] text-sm px-6 py-3 font-medium inline-flex items-center gap-2">
            Add birth details <ArrowRight size={16} />
          </Link>
          <Link to="/how-it-works" className="btn-pill border border-white/25 text-white text-sm px-6 py-3 hover:bg-white/10">How it works</Link>
        </div>
      </PageHero>

      {/* The Fusion Explanation */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <motion.div
            initial={{ opacity: 0, x: -16 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="rounded-2xl p-8 border border-stone-200 bg-white"
          >
            <Moon className="text-amber-700" />
            <div className="overline mt-3">The Vedic Half</div>
            <h2 className="font-serif-display text-3xl text-[#1E1B4B] mt-2 leading-tight">36 gunas of Ashtakoot</h2>
            <p className="mt-4 text-stone-700 leading-relaxed">
              Ashtakoot Guna Milan is the sacred method of matching two janma-kundalis across eight
              cosmic dimensions — Varna, Vashya, Tara, Yoni, Graha Maitri, Gana, Bhakoot, and Nadi.
              Each dimension carries a specific weight; the sum out of 36 tells us how the stars
              have written this pairing.
            </p>
            <p className="mt-3 text-stone-700 leading-relaxed">
              We also flag <em>Mangal Dosha</em> and provide guidance where relevant — with quiet
              respect for tradition, and without fear-mongering.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 16 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="rounded-2xl p-8 relative overflow-hidden text-white"
            style={{ background: "linear-gradient(135deg, #1E1B4B 0%, #12103A 100%)" }}
          >
            <Sparkles className="text-amber-300" />
            <div className="overline mt-3" style={{ color: "#FCD34D" }}>The Intelligence Half</div>
            <h2 className="font-serif-display text-3xl mt-2 leading-tight">8 dimensions of the science</h2>
            <p className="mt-4 text-stone-300 leading-relaxed">
              Our AI reads across eight psychological dimensions grounded in decades of relationship
              research — attachment theory, communication science, family systems, and long-term
              marital-satisfaction studies. Every score is explainable.
            </p>
            <p className="mt-3 text-stone-300 leading-relaxed">
              You&apos;ll see <em>why</em> the AI thinks you&apos;re compatible — not just a number.
              Strengths, friction points, and conversations to have before you say yes.
            </p>
          </motion.div>
        </div>

        {/* Guna table */}
        <div className="mt-16">
          <div className="overline">The 36 Gunas of Ashtakoot</div>
          <h3 className="font-serif-display text-3xl lg:text-4xl text-[#1E1B4B] mt-2">Every star aligned. Every dimension explained.</h3>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {GUNAS.map((g, i) => (
              <motion.div
                key={g.name}
                initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: i * 0.03 }}
                className="border border-stone-200 rounded-xl bg-white p-5"
              >
                <div className="flex items-baseline justify-between">
                  <div className="font-serif-display text-lg text-[#1E1B4B]">{g.name}</div>
                  <div className="font-serif-display text-amber-700 text-2xl">{g.max}</div>
                </div>
                <p className="mt-2 text-xs text-stone-600 leading-relaxed">{g.desc}</p>
              </motion.div>
            ))}
          </div>
          <p className="mt-6 text-xs text-stone-500 italic">
            Total possible: 36 gunas. 18+ is considered auspicious. Under 18 is not a
            &quot;rejection&quot; — it&apos;s a signal to have deeper conversations.
          </p>
        </div>

        {/* AI dimensions */}
        <div className="mt-16 rounded-2xl border border-stone-200 bg-white p-8 lg:p-10">
          <div className="overline">The 8 AI Dimensions</div>
          <h3 className="font-serif-display text-3xl text-[#1E1B4B] mt-2">Where psychology meets prediction.</h3>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {AI_DIMENSIONS.map((d) => (
              <div key={d} className="flex items-start gap-2 text-sm text-stone-700">
                <span className="text-amber-700">◆</span>{d}
              </div>
            ))}
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-16 rounded-2xl p-8 bg-amber-50 border border-amber-200">
          <div className="overline">A quiet note</div>
          <p className="mt-3 text-stone-700 leading-relaxed">
            Kundali fusion at Anubandh.ai is <strong>illustrative and reflective</strong>. Our
            calculations use ephemeris-grade libraries where available, and heuristic
            approximations elsewhere. We are working towards astrologer-grade accuracy with
            <em> pyswisseph</em> integration. For life-altering decisions, please supplement our
            insights with a qualified jyotishi, a licensed counsellor, and honest conversation
            with your family.
          </p>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
